import { Component, Inject, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormModalBaseComponent } from '../../../../../../core/base/components/form-modal-base/form-modal-base.component';
import { EstablishmentModel } from '../../../../../../core/models/establishment.model';
import { EstablishmentService } from '../../establishment.service';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { AutocompleteService } from '../../../../../../core/services/scroll.service';
import { GenericPrestationService } from '../../../../settings/generic-prestation/generic-prestation.service';
import { GenericProductService } from '../../../../settings/generic-product/generic-product.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ProductCategoryService } from '../../../../settings/product-category/product-category.service';
import { PrestationCategoryService } from '../../../../settings/prestation-category/prestation-category.service';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'vex-establishment-needs-select',
  templateUrl: './establishment-needs-select.component.html',
  styleUrls: ['./establishment-needs-select.component.scss'],
})
export class EstablishmentNeedsSelectModalComponent
  extends FormModalBaseComponent<EstablishmentModel>
  implements OnInit, OnDestroy
{
  form: UntypedFormGroup;
  establishment: EstablishmentModel;
  isGenericPrestation = false;
  title: string;
  categories$: Observable<any[]>;
  genericItems$: Observable<any[]>;
  items$: Observable<any[]>; // Pour form-input-base
  genericItemSearchTerm = '';

  // BehaviorSubject local pour contrôler manuellement les émissions
  private localGenericItems$ = new BehaviorSubject<any[]>([]);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<FormModalBaseComponent<EstablishmentModel>>,
    public service: EstablishmentService,
    public autocompleteService: AutocompleteService,
    public genericPrestationService: GenericPrestationService,
    public genericProductService: GenericProductService,
    private productCategoryService: ProductCategoryService,
    private prestationCategoryService: PrestationCategoryService,
    private cdr: ChangeDetectorRef,
  ) {
    super(data, dialogRef, service);

    this.establishment = this.data.defaults;
    this.isGenericPrestation = this.data.isGenericPrestation;
    this.title = this.data.title;

    this.form = new UntypedFormGroup({
      categories: new UntypedFormControl([]), // Optionnel - sert uniquement de filtre
      genericItems: new UntypedFormControl(null, [Validators.required]), // Le seul champ obligatoire
    });
  }

  onCategorySelect(categories: any[]) {
    this.refreshGenericItems(categories);
  }

  ngOnInit() {
    super.ngOnInit();
    this.loadCategories();

    // SOLUTION: Utiliser items$ au lieu de genericItems$ pour que form-input-base puisse créer filteredStates$
    this.items$ = this.localGenericItems$.asObservable();

    this.autocompleteGenericItems();

    // Écouter les changements de catégories pour filtrer les éléments génériques
    // ATTENTION: Ne pas utiliser valueChanges car cela crée un double appel avec onCategorySelect
    // this.form.controls["categories"].valueChanges.subscribe((categories) => {
    //     this.refreshGenericItems(categories);
    // });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    // Nettoyer notre BehaviorSubject local
    this.localGenericItems$.complete();
  }

  getItems$(filterField: string) {
    const cacheKey = 'needs-' + filterField;
    const items$ = this.autocompleteService.getItems$(cacheKey);
    // S'assurer de ne jamais retourner undefined
    const result = items$ || of([]);
    return result;
  }

  getItemType(): string {
    return this.isGenericPrestation ? 'genericPrestation' : 'genericProduct';
  }

  getCategoryType(): string {
    return this.isGenericPrestation ? 'prestationCategory' : 'productCategory';
  }

  autocompleteGenericItems(search: string = '', pageToGo?: 'next' | 'previous', categories?: any[]) {
    const itemType = this.getItemType();
    const categoryType = this.getCategoryType();

    if (!pageToGo) {
      this.genericItemSearchTerm = search;
    }

    const query = pageToGo ? this.genericItemSearchTerm : search;
    const selectedCategories = categories || this.form.controls['categories'].value || [];

    const filters: any = {
      excludedIds: this.getExistingItemIds(),
    };

    if (this.isGenericPrestation) {
    } else {
      filters.stockable = true;
    }

    // Ne filtrer par catégorie que si des catégories sont sélectionnées
    if (selectedCategories.length > 0) {
      filters.categoryIds = selectedCategories.map((c: any) => c._id);
    } else {
    }

    const subscription = this.autocompleteService.autocompleteInfiniteScrollNeeds(
      query,
      itemType,
      '_id',
      pageToGo,
      (page, size) => {
        // Le filtrage se fait uniquement côté backend via filters.categoryIds
        // Plus de double filtrage côté frontend
        const serviceCall = this.isGenericPrestation
          ? this.genericPrestationService.findAll(page, size, 'label', 'asc', query, filters)
          : this.genericProductService.findAll(page, size, 'label', 'asc', query, filters);

        return serviceCall as any;
      },
    );

    if (subscription) {
      subscription.subscribe({
        next: result => {
          // SOLUTION FINALE: Émettre directement via notre BehaviorSubject local
          setTimeout(() => {
            // Récupérer les données du cache
            const cacheKey = `needs-${itemType}`;
            const cachedObservable = this.autocompleteService.getItems$(cacheKey);

            if (cachedObservable) {
              // S'abonner une seule fois pour récupérer les données actuelles
              cachedObservable.subscribe(data => {
                // Émettre manuellement vers notre BehaviorSubject
                this.localGenericItems$.next(data || []);
              });
            } else {
              // Fallback avec tableau vide
              this.localGenericItems$.next([]);
            }

            // Forcer la détection de changement
            this.cdr.markForCheck();
            this.cdr.detectChanges();
          }, 50);
        },
        error: error => {},
      });
    } else {
    }
  }

  refreshGenericItems(categories?: any[]) {
    const itemType = this.getItemType();
    // Reset complet du cache et de la recherche
    this.autocompleteService.resetItem(`needs-${itemType}`);
    this.genericItemSearchTerm = '';

    // Ne pas réinitialiser genericItems$ - garder l'Observable existant
    // pour éviter de casser la référence dans le composant form-search-multiple

    // Recharger immédiatement avec une recherche vide pour obtenir les résultats
    this.autocompleteGenericItems('', undefined, categories);
  }

  onScrollLine(pageToGo: 'next' | 'previous', filterField: string) {
    if (filterField === this.getItemType()) {
      this.autocompleteGenericItems(this.genericItemSearchTerm, pageToGo);
    } else {
      console.error('Unknown filterField:', filterField);
    }
  }

  loadCategories(search: string = '') {
    const categoryService = this.isGenericPrestation ? this.prestationCategoryService : this.productCategoryService;
    this.categories$ = categoryService.findAll(null, null, 'label', 'asc', search).pipe(map((res: any) => res.data));
  }

  createItem() {
    this.loading = true;

    if (!this.form.valid) {
      this.form.markAllAsTouched();
      this.loading = false;
      return;
    }

    const items = this.form.controls['genericItems'].value.map(
      ({ _id, label, technicalIdentifier, category, productCategory, prestationCategory }) => ({
        _id,
        label,
        technicalIdentifier,
        category,
        productCategory,
        prestationCategory,
      }),
    );

    const serviceCall = this.isGenericPrestation
      ? this.service.addGenericPrestation(this.establishment._id, items)
      : this.service.addGenericProduct(this.establishment._id, items);

    serviceCall.subscribe({
      next: res => {
        this.close(res);
        this.loading = false;
        // Reset autocomplete cache
        this.autocompleteService.resetItem('needs-genericPrestation');
        this.autocompleteService.resetItem('needs-genericProduct');
      },
      error: err => {
        this.setErrorsMessages(err.error.errors);
        this.loading = false;
      },
    });
  }

  private getExistingItemIds(): string[] {
    if (this.isGenericPrestation) {
      return this.establishment.genericPrestations?.map(x => x._id) || [];
    } else {
      return this.establishment.genericProducts?.map(x => x._id) || [];
    }
  }

  // Méthodes utilitaires supprimées car le filtrage se fait maintenant uniquement côté backend
}
